import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthProvider';

export interface CarIssue {
  id: string;
  title: string;
  description: string;
  date: string;
  images?: string[]; // GCS paths
  videos?: string[]; // GCS paths
  aiSummary?: string;
  status: 'draft' | 'open' | 'recurred' | 'resolved';
  notes?: string;
  aiQuestions?: string[];
  aiAnswers?: string[];
  aiCauses?: string[];
  aiRecommendations?: string[];
  location?: string;
  urgencyLevel?: number; // 1-5 scale
  possibleCauses?: string[];
  diyCheck?: string | string[];
  explanation?: string;
  resolvedBy?: string; // repairId that resolved this issue
  recurrences?: Recurrence[]; // Added recurrences field
}

export interface Recurrence {
  id: string;
  date: string;
  notes: string;
}

export interface Repair {
  id: string;
  title: string;
  date: string;
  mechanicName?: string;
  garageName?: string;
  cost?: number;
  description?: string;
  receiptImage?: string;
  resolvedIssues?: string[]; // issue IDs that were resolved by this repair
  partiallyResolvedIssues?: string[]; // issue IDs that were partially resolved
  addedByMechanic?: boolean;
  mechanicAddedDate?: string;
}

export interface Car {
  id: string;
  brand: string;
  model: string;
  year: number;
  nickname?: string;
  vin?: string;
  licensePlate?: string;
  mileage?: number;
  transmission?: 'manual' | 'automatic';
  fuelType?: 'gasoline' | 'diesel' | 'electric' | 'hybrid' | 'lpg' | 'cng';
  maintenanceHistory?: { date: string; service: string; mileage: number }[];
  issues?: CarIssue[];
  repairs?: Repair[];
  image_url?: string;
  image_path?: string; // <-- add this line
  created_by?: string; // User ID of the original owner
  // Added fields for extended car details
  ownershipType?: string;
  insuranceProvider?: string;
  insuranceExpiry?: string | null;
  lastServiceDate?: string | null;
  // Tires
  winterTiresBrand?: string;
  winterTiresSize?: string;
  summerTiresBrand?: string;
  summerTiresSize?: string;
  tiresDotCode?: string;
  tiresLastChange?: string | null;
  tirePressure?: string | null;
  // Wipers
  wiperType?: string | null;
  wiperFrontSize?: string | null;
  wiperRearSize?: string | null;
  wiperLastReplacement?: string | null;
  // New wiper fields
  wiperDriversSideSize?: string | null;
  wiperPassangerSideSize?: string | null;
  // OCTA
  octaPolicyNumber?: string;
  octaExpiry?: string | null;
  octaProvider?: string;
  octaClaimsHotline?: string;
  octaMediaPaths?: string[];
  // KASKO
  kaskoPolicyNumber?: string;
  kaskoExpiry?: string | null;
  kaskoProvider?: string;
  kaskoClaimsHotline?: string;
  kaskoMediaPaths?: string[];
}

interface CarContextType {
  cars: Car[];
  mainCarId: string | null;
  addCar: (car: Omit<Car, 'id'>) => Promise<void>;
  updateCar: (id: string, car: Partial<Car>) => Promise<void>;
  deleteCar: (id: string) => Promise<void>;
  restoreCar: (id: string) => Promise<void>;
  setMainCar: (id: string | null) => void;
  getMainCar: () => Car | undefined;
  addIssue: (carId: string, issue: Omit<CarIssue, 'id'>) => Promise<string>;
  addIssueToCar: (carId: string, issue: CarIssue) => void; // Add this method for in-memory updates
  updateIssue: (carId: string, issueId: string, issue: Partial<CarIssue>) => void;
  deleteIssue: (carId: string, issueId: string) => void;
  addRepair: (carId: string, repair: Omit<Repair, 'id'>) => string;
  updateRepair: (carId: string, repairId: string, repair: Partial<Repair>) => void;
  deleteRepair: (carId: string, repairId: string) => void;
  getRepair: (carId: string, repairId: string) => Repair | undefined;
  addRecurrence: (carId: string, issueId: string, recurrence: Omit<Recurrence, 'id'>) => string;
  deleteRecurrence: (carId: string, issueId: string, recurrenceId: string) => void;
  // Car sharing functions
  generateShareLink: (carId: string) => Promise<{ shareLink: string; expiresAt: string }>;
  acceptShareLink: (token: string) => Promise<{ carId: string }>;
  removeSharedUser: (carId: string, userIdToRemove?: string) => Promise<void>;
  transferOwnership: (carId: string, newOwnerId: string, removeFromOriginalOwner: boolean) => Promise<void>;
}

export const CarContext = createContext<CarContextType | undefined>(undefined);

export const useCar = () => {
  const context = useContext(CarContext);
  if (!context) {
    throw new Error('useCar must be used within a CarProvider');
  }
  return context;
};

export const CarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cars, setCars] = useState<Car[]>([]); // Remove localStorage initialization
  
  const [mainCarId, setMainCarId] = useState<string | null>(null); // Remove localStorage initialization

  const { user } = useAuth();

  // Remove localStorage effects - we'll use Supabase as source of truth
  // useEffect(() => {
  //   localStorage.setItem('cars', JSON.stringify(cars));
  // }, [cars]);

  // useEffect(() => {
  //   if (mainCarId) {
  //     localStorage.setItem('mainCarId', mainCarId);
  //   } else {
  //     localStorage.removeItem('mainCarId');
  //   }
  // }, [mainCarId]);

  useEffect(() => {
    if (user) {
      fetchCarsFromSupabase();
    }
    if (!user) {
      setCars([]);
      setMainCarId(null);
    }
  }, [user]);

  const fetchCarsFromSupabase = async () => {
    if (!user) {
      console.log('fetchCarsFromSupabase: No user, returning early');
      return;
    }
    
    console.log('fetchCarsFromSupabase: Starting fetch for user:', user.id);

    // Fetch user preference for main car (server source of truth)
    const { data: userPref, error: userPrefError } = await supabase
      .from('users')
      .select('main_car_id')
      .eq('id', user.id)
      .maybeSingle();
    if (userPrefError) {
      console.warn('fetchCarsFromSupabase: Failed to fetch user main_car_id:', userPrefError);
    } else {
      setMainCarId((userPref as any)?.main_car_id ?? null);
    }
    
    // Fetch all cars user has access to via user_cars
    const { data: userCarsData, error: userCarsError } = await supabase
      .from('user_cars')
      .select('car_id, cars(*)')
      .eq('user_id', user.id);
      
    if (userCarsError) {
      console.error("Error fetching user cars:", userCarsError);
    } else {
      console.log('fetchCarsFromSupabase: userCarsData:', userCarsData?.length || 0, 'entries');
    }
    
    // Also fetch cars where user is the creator (for backward compatibility with pre-sharing cars)
    const { data: createdCarsData, error: createdCarsError } = await supabase
      .from('cars')
      .select('*')
      .eq('created_by', user.id)
      .is('deleted_at', null);
    
    if (createdCarsError) {
      console.error("Error fetching created cars:", createdCarsError);
    } else {
      console.log('fetchCarsFromSupabase: createdCarsData:', createdCarsData?.length || 0, 'entries');
    }
    
    // Combine both sources and deduplicate by car id
    const carMap = new Map<string, any>();
    
    // Add cars from user_cars (exclude soft-deleted)
    if (userCarsData) {
      userCarsData.forEach((row: any) => {
        if (row.cars && row.cars.deleted_at == null) {
          carMap.set(row.car_id, row.cars);
        }
      });
    }
    
    // Add cars from created_by (will overwrite if already in map, but that's fine)
    if (createdCarsData) {
      createdCarsData.forEach((car: any) => {
        carMap.set(car.id, car);
      });
    }
    
    // Convert map to array
    const allCars = Array.from(carMap.values());
    console.log('fetchCarsFromSupabase: Combined allCars:', allCars.length, 'cars');
    
    if (allCars.length === 0) {
      console.warn('fetchCarsFromSupabase: No cars found! Check RLS policies and database entries.');
    }
    
    // Fetch user-specific preferences (nicknames) for these cars
    const carIds = allCars.map((car: any) => car.id);
    let preferencesData = null;
    if (carIds.length > 0) {
      const { data } = await supabase
        .from('user_car_preferences')
        .select('car_id, nickname')
        .eq('user_id', user.id)
        .in('car_id', carIds);
      preferencesData = data;
    }
    
    // Create a map of car_id -> nickname for quick lookup
    const nicknameMap = new Map<string, string>();
    if (preferencesData) {
      preferencesData.forEach((pref: any) => {
        if (pref.nickname) {
          nicknameMap.set(pref.car_id, pref.nickname);
        }
      });
    }
    
    // Map database columns to frontend field names
    const flatCars = allCars.map((car: any) => {
      // Use user-specific nickname if available, otherwise fall back to car's default nickname
      const displayNickname = nicknameMap.get(car.id) || car.nickname;
      
      return {
        ...car,
        nickname: displayNickname, // Use user-specific or default nickname
        created_by: car.created_by, // Include created_by to check ownership
        brand: car.make, // Map make to brand
        licensePlate: car.license_plate, // Map license_plate to licensePlate
        fuelType: car.fuel_type, // Map fuel_type to fuelType
        ownershipType: car.ownership_type, // Map ownership_type to ownershipType
        insuranceProvider: car.insurance_provider, // Map insurance_provider to insuranceProvider
        insuranceExpiry: car.insurance_expiry, // Map insurance_expiry to insuranceExpiry
        lastServiceDate: car.last_service_date, // Map last_service_date to lastServiceDate
        winterTiresBrand: car.winter_tires_brand, // Map winter_tires_brand to winterTiresBrand
        winterTiresSize: car.winter_tires_size, // Map winter_tires_size to winterTiresSize
        summerTiresBrand: car.summer_tires_brand, // Map summer_tires_brand to summerTiresBrand
        summerTiresSize: car.summer_tires_size, // Map summer_tires_size to summerTiresSize
      tirePressure: car.tire_pressure,
      wiperType: car.wiper_type,
      wiperFrontSize: car.wiper_front_size,
      wiperRearSize: car.wiper_rear_size,
      wiperLastReplacement: car.wiper_last_replacement,
      wiperDriversSideSize: car.wiper_drivers_side_size,
      wiperPassangerSideSize: car.wiper_passanger_side_size,
        octaPolicyNumber: car.octa_policy_number, // Map octa_policy_number to octaPolicyNumber
        octaExpiry: car.octa_expiry, // Map octa_expiry to octaExpiry
        octaProvider: car.octa_provider, // Map octa_provider to octaProvider
        octaClaimsHotline: car.octa_claims_hotline, // Map octa_claims_hotline
        octaMediaPaths: car.octa_media_paths ?? [],
        kaskoPolicyNumber: car.kasko_policy_number, // Map kasko_policy_number to kaskoPolicyNumber
        kaskoExpiry: car.kasko_expiry, // Map kasko_expiry to kaskoExpiry
        kaskoProvider: car.kasko_provider, // Map kasko_provider to kaskoProvider
        kaskoClaimsHotline: car.kasko_claims_hotline, // Map kasko_claims_hotline
        kaskoMediaPaths: car.kasko_media_paths ?? [],
      };
    });
    
    setCars(flatCars);
    
    // After fetching cars, fetch symptoms for each car
    await fetchSymptomsFromSupabase(flatCars);
  };

  const fetchSymptomsFromSupabase = async (carsToUpdate?: Car[]) => {
    if (!user) return;
    
    const carsToProcess = carsToUpdate || cars;
    if (carsToProcess.length === 0) return;
    
    try {
      // Fetch all symptoms for cars the user has access to (not just created by user)
      // This includes shared cars via user_cars
      const carIds = carsToProcess.map(car => car.id);
      const { data: symptomsData, error } = await supabase
        .from('symptoms')
        .select('*')
        .in('car_id', carIds)
        .is('deleted_at', null);
        
      if (error) {
        console.error("Error fetching symptoms:", error);
        return;
      }
      
      // Group symptoms by car_id
      const symptomsByCar = symptomsData.reduce((acc: Record<string, CarIssue[]>, symptom: any) => {
        const carId = symptom.car_id;
        if (!acc[carId]) {
          acc[carId] = [];
        }
        
        // Map database symptom to CarIssue format
        const carIssue: CarIssue = {
          id: symptom.id,
          title: symptom.title || symptom.description.slice(0, 50),
          description: symptom.description,
          date: symptom.created_at,
          images: [], // Will be populated from media table if needed
          videos: [],
          aiSummary: symptom.problems_stated,
          aiQuestions: symptom.questions || [],
          aiAnswers: symptom.answers || [],
          status: symptom.status || 'open',
          notes: symptom.notes || '',
          urgencyLevel: symptom.urgency || 3,
          possibleCauses: symptom.possible_causes || [],
          diyCheck: symptom.diy_check || '',
          explanation: symptom.explanation || '',
          recurrences: [], // Will be populated from symptom_recurrences table if needed
        };
        
        acc[carId].push(carIssue);
        return acc;
      }, {});
      
      // Update cars with their symptoms
      setCars(prevCars => prevCars.map(car => ({
        ...car,
        issues: symptomsByCar[car.id] || []
      })));
      
      console.log('✅ Symptoms loaded from Supabase for', Object.keys(symptomsByCar).length, 'cars');
    } catch (error) {
      console.error("Error in fetchSymptomsFromSupabase:", error);
    }
  };

  const addCar = async (car: Omit<Car, 'id'>) => {
    if (!user) return;

    // Map frontend field names to database column names with proper null handling
    const dbCar: any = {
      make: car.brand || null,
      model: car.model || null,
      year: car.year || null,
      nickname: car.nickname || null,
      license_plate: car.licensePlate || null,
      vin: car.vin || null,
      mileage: car.mileage || null,
      transmission: car.transmission || null,
      fuel_type: car.fuelType || null,
      ownership_type: car.ownershipType || null,
      insurance_provider: car.insuranceProvider || null,
      insurance_expiry: car.insuranceExpiry || null,
      last_service_date: car.lastServiceDate || null,
      image_url: car.image_url || null,
      image_path: car.image_path || null, // Add image_path to dbCar
      winter_tires_brand: car.winterTiresBrand || null,
      winter_tires_size: car.winterTiresSize || null,
      summer_tires_brand: car.summerTiresBrand || null,
      summer_tires_size: car.summerTiresSize || null,
      tire_pressure: car.tirePressure || null,
      wiper_type: car.wiperType || null,
      wiper_front_size: car.wiperFrontSize || null,
      wiper_rear_size: car.wiperRearSize || null,
      wiper_last_replacement: car.wiperLastReplacement || null,
      octa_policy_number: car.octaPolicyNumber || null,
      octa_expiry: car.octaExpiry || null,
      octa_provider: car.octaProvider || null,
      octa_claims_hotline: car.octaClaimsHotline || null,
      octa_media_paths: car.octaMediaPaths ?? [],
      kasko_policy_number: car.kaskoPolicyNumber || null,
      kasko_expiry: car.kaskoExpiry || null,
      kasko_provider: car.kaskoProvider || null,
      kasko_claims_hotline: car.kaskoClaimsHotline || null,
      kasko_media_paths: car.kaskoMediaPaths ?? [],
      created_by: user.id,
    };

    // Insert into database
    const { data, error } = await supabase
      .from('cars')
      .insert(dbCar)
      .select()
      .single();

    if (error) {
      console.error('Error adding car to database:', error);
      throw new Error(`Failed to add car: ${error.message}`);
    }

    // Create user_car relationship
    const { error: userCarError } = await supabase
      .from('user_cars')
      .insert({
        user_id: user.id,
        car_id: data.id,
      });

    if (userCarError) {
      console.error('Error creating user_car relationship:', userCarError);
      throw new Error(`Failed to link car to user: ${userCarError.message}`);
    }

    // Map back to frontend format and add to local state
    const newCar = {
      ...data,
      brand: data.make,
      licensePlate: data.license_plate,
      fuelType: data.fuel_type,
      ownershipType: data.ownership_type,
      insuranceProvider: data.insurance_provider,
      insuranceExpiry: data.insurance_expiry,
      lastServiceDate: data.last_service_date,
      image_path: data.image_path, // Add image_path to newCar
      winterTiresBrand: data.winter_tires_brand,
      winterTiresSize: data.winter_tires_size,
      summerTiresBrand: data.summer_tires_brand,
      summerTiresSize: data.summer_tires_size,
      tirePressure: data.tire_pressure,
      wiperType: data.wiper_type,
      wiperFrontSize: data.wiper_front_size,
      wiperRearSize: data.wiper_rear_size,
      wiperLastReplacement: data.wiper_last_replacement,
      octaPolicyNumber: data.octa_policy_number,
      octaExpiry: data.octa_expiry,
      octaProvider: data.octa_provider,
      octaClaimsHotline: data.octa_claims_hotline,
      octaMediaPaths: data.octa_media_paths ?? [],
      kaskoPolicyNumber: data.kasko_policy_number,
      kaskoExpiry: data.kasko_expiry,
      kaskoProvider: data.kasko_provider,
      kaskoClaimsHotline: data.kasko_claims_hotline,
      kaskoMediaPaths: data.kasko_media_paths ?? [],
      issues: [],
      repairs: [],
    };

    setCars([...cars, newCar]);
    
    if (cars.length === 0) {
      setMainCarId(newCar.id);
      // localStorage.setItem('mainCarId', newCar.id); // Removed localStorage
    }
  };

  const updateCar = async (id: string, updates: Partial<Car>) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Find the car to check if user is the owner
    const car = cars.find(c => c.id === id);
    if (!car) {
      throw new Error('Car not found');
    }

    const isOwner = car.created_by === user.id;

    // Map frontend field names to database column names
    const dbUpdates: any = {};
    const frontendUpdates: any = {};
    
    // Handle nickname separately based on ownership
    let nicknameUpdateHandled = false;
    if (updates.nickname !== undefined) {
      nicknameUpdateHandled = true;
      frontendUpdates.nickname = updates.nickname || undefined;
      
      if (isOwner) {
        // Original owner: update global car nickname
        dbUpdates.nickname = updates.nickname || null;
      }
      // For collaborators, we'll update user_car_preferences separately below
    }
    
    // Handle other fields - only owners can update car details
    if (isOwner) {
      if (updates.brand !== undefined) { dbUpdates.make = updates.brand || null; frontendUpdates.brand = updates.brand || undefined; }
      if (updates.model !== undefined) dbUpdates.model = updates.model || null;
      if (updates.year !== undefined) dbUpdates.year = updates.year || null;
    if (updates.licensePlate !== undefined) { dbUpdates.license_plate = updates.licensePlate || null; frontendUpdates.licensePlate = updates.licensePlate || undefined; }
    if (updates.vin !== undefined) { dbUpdates.vin = updates.vin || null; frontendUpdates.vin = updates.vin || undefined; }
    if (updates.mileage !== undefined) { dbUpdates.mileage = updates.mileage || null; frontendUpdates.mileage = updates.mileage || undefined; }
    if (updates.transmission !== undefined) { dbUpdates.transmission = updates.transmission || null; frontendUpdates.transmission = updates.transmission || undefined; }
    if (updates.fuelType !== undefined) { dbUpdates.fuel_type = updates.fuelType || null; frontendUpdates.fuelType = updates.fuelType || undefined; }
    if (updates.ownershipType !== undefined) dbUpdates.ownership_type = updates.ownershipType || null;
    if (updates.insuranceProvider !== undefined) dbUpdates.insurance_provider = updates.insuranceProvider || null;
    if (updates.insuranceExpiry !== undefined) dbUpdates.insurance_expiry = updates.insuranceExpiry || null;
    if (updates.lastServiceDate !== undefined) { dbUpdates.last_service_date = updates.lastServiceDate || null; frontendUpdates.lastServiceDate = updates.lastServiceDate || undefined; }
    if (updates.image_url !== undefined) { dbUpdates.image_url = updates.image_url || null; frontendUpdates.image_url = updates.image_url || undefined; }
    if (updates.image_path !== undefined) { dbUpdates.image_path = updates.image_path || null; frontendUpdates.image_path = updates.image_path || undefined; }
    if (updates.winterTiresBrand !== undefined) { dbUpdates.winter_tires_brand = updates.winterTiresBrand || null; frontendUpdates.winterTiresBrand = updates.winterTiresBrand || undefined; }
    if (updates.winterTiresSize !== undefined) { dbUpdates.winter_tires_size = updates.winterTiresSize || null; frontendUpdates.winterTiresSize = updates.winterTiresSize || undefined; }
    if (updates.summerTiresBrand !== undefined) { dbUpdates.summer_tires_brand = updates.summerTiresBrand || null; frontendUpdates.summerTiresBrand = updates.summerTiresBrand || undefined; }
    if (updates.summerTiresSize !== undefined) { dbUpdates.summer_tires_size = updates.summerTiresSize || null; frontendUpdates.summerTiresSize = updates.summerTiresSize || undefined; }
    if (updates.tirePressure !== undefined) { dbUpdates.tire_pressure = updates.tirePressure || null; frontendUpdates.tirePressure = updates.tirePressure || undefined; }
    if (updates.wiperType !== undefined) { dbUpdates.wiper_type = updates.wiperType || null; frontendUpdates.wiperType = updates.wiperType || undefined; }
    if (updates.wiperFrontSize !== undefined) { dbUpdates.wiper_front_size = updates.wiperFrontSize || null; frontendUpdates.wiperFrontSize = updates.wiperFrontSize || undefined; }
    if (updates.wiperRearSize !== undefined) { dbUpdates.wiper_rear_size = updates.wiperRearSize || null; frontendUpdates.wiperRearSize = updates.wiperRearSize || undefined; }
    if (updates.wiperLastReplacement !== undefined) { dbUpdates.wiper_last_replacement = updates.wiperLastReplacement || null; frontendUpdates.wiperLastReplacement = updates.wiperLastReplacement || undefined; }
    if ((updates as any).wiperDriversSideSize !== undefined) {
      dbUpdates.wiper_drivers_side_size = (updates as any).wiperDriversSideSize || null;
      frontendUpdates.wiperDriversSideSize = (updates as any).wiperDriversSideSize || undefined;
    }
    if ((updates as any).wiperPassangerSideSize !== undefined) {
      dbUpdates.wiper_passanger_side_size = (updates as any).wiperPassangerSideSize || null;
      frontendUpdates.wiperPassangerSideSize = (updates as any).wiperPassangerSideSize || undefined;
    }
    if (updates.octaPolicyNumber !== undefined) dbUpdates.octa_policy_number = updates.octaPolicyNumber || null;
    if (updates.octaExpiry !== undefined) dbUpdates.octa_expiry = updates.octaExpiry || null;
    if (updates.octaProvider !== undefined) dbUpdates.octa_provider = updates.octaProvider || null;
    if (updates.octaClaimsHotline !== undefined) dbUpdates.octa_claims_hotline = updates.octaClaimsHotline || null;
    if (updates.octaMediaPaths !== undefined) {
      dbUpdates.octa_media_paths = (updates.octaMediaPaths?.length ? updates.octaMediaPaths : []) || [];
      frontendUpdates.octaMediaPaths = updates.octaMediaPaths;
    }
    if (updates.kaskoPolicyNumber !== undefined) dbUpdates.kasko_policy_number = updates.kaskoPolicyNumber || null;
    if (updates.kaskoExpiry !== undefined) dbUpdates.kasko_expiry = updates.kaskoExpiry || null;
    if (updates.kaskoProvider !== undefined) dbUpdates.kasko_provider = updates.kaskoProvider || null;
    if (updates.kaskoClaimsHotline !== undefined) dbUpdates.kasko_claims_hotline = updates.kaskoClaimsHotline || null;
    if (updates.kaskoMediaPaths !== undefined) {
      dbUpdates.kasko_media_paths = (updates.kaskoMediaPaths?.length ? updates.kaskoMediaPaths : []) || [];
      frontendUpdates.kaskoMediaPaths = updates.kaskoMediaPaths;
    }
    }

    // Update cars table (only for owners, and only if there are updates)
    if (isOwner && Object.keys(dbUpdates).length > 0) {
      const { error } = await supabase
        .from('cars')
        .update(dbUpdates)
        .eq('id', id);

      if (error) {
        console.error('Error updating car in database:', error);
        throw new Error(`Failed to update car: ${error.message}`);
      }

      // Notify partners so they see "Owner updated car" in New Activity
      const dbToFrontend: Record<string, keyof Car> = {
        make: 'brand',
        model: 'model',
        year: 'year',
        license_plate: 'licensePlate',
        vin: 'vin',
        mileage: 'mileage',
        transmission: 'transmission',
        fuel_type: 'fuelType',
        ownership_type: 'ownershipType',
        insurance_provider: 'insuranceProvider',
        insurance_expiry: 'insuranceExpiry',
        last_service_date: 'lastServiceDate',
        image_url: 'image_url',
        image_path: 'image_path',
        winter_tires_brand: 'winterTiresBrand',
        winter_tires_size: 'winterTiresSize',
        summer_tires_brand: 'summerTiresBrand',
        summer_tires_size: 'summerTiresSize',
        tire_pressure: 'tirePressure',
        wiper_type: 'wiperType',
        wiper_front_size: 'wiperFrontSize',
        wiper_rear_size: 'wiperRearSize',
        wiper_last_replacement: 'wiperLastReplacement',
        wiper_drivers_side_size: 'wiperDriversSideSize',
        wiper_passanger_side_size: 'wiperPassangerSideSize',
        octa_policy_number: 'octaPolicyNumber',
        octa_expiry: 'octaExpiry',
        octa_provider: 'octaProvider',
        octa_claims_hotline: 'octaClaimsHotline',
        octa_media_paths: 'octaMediaPaths',
        kasko_policy_number: 'kaskoPolicyNumber',
        kasko_expiry: 'kaskoExpiry',
        kasko_provider: 'kaskoProvider',
        kasko_claims_hotline: 'kaskoClaimsHotline',
        kasko_media_paths: 'kaskoMediaPaths',
      };
      const changes: Record<string, { old: unknown; new: unknown }> = {};
      for (const [dbCol, newVal] of Object.entries(dbUpdates)) {
        const frontendKey = dbToFrontend[dbCol as keyof typeof dbToFrontend];
        const oldVal = frontendKey ? (car as any)[frontendKey] : undefined;
        changes[dbCol] = { old: oldVal ?? null, new: newVal ?? null };
      }
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          await fetch(`${supabaseUrl}/functions/v1/notify-partners-car-updated`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ carId: id, changes }),
          });
        }
      } catch (notifyErr) {
        console.warn('Failed to notify partners of car update:', notifyErr);
      }
    }

    // Update user_car_preferences for nickname (for both owners and collaborators)
    if (nicknameUpdateHandled) {
      const { error: prefError } = await supabase
        .from('user_car_preferences')
        .upsert({
          user_id: user.id,
          car_id: id,
          nickname: updates.nickname || null,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id,car_id'
        });

      if (prefError) {
        console.error('Error updating user car preference:', prefError);
        throw new Error(`Failed to update nickname: ${prefError.message}`);
      }
    }

    // Update local state with the original frontend field names
    setCars(cars.map(car => car.id === id ? { ...car, ...frontendUpdates } : car));
  };

  const deleteCar = async (id: string) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      // Soft delete: set deleted_at so the car is hidden but can be restored later
      const { error: updateError } = await supabase
        .from('cars')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);

      if (updateError) {
        // If update fails (e.g. RLS: not the only manager), remove user from car instead
        if (updateError.code === '42501' || updateError.message?.includes('policy')) {
          await removeSharedUser(id, user.id);
          setCars(cars.filter(car => car.id !== id));
          if (mainCarId === id) {
            setMainCarId(cars.length > 1 ? cars.find(car => car.id !== id)?.id || null : null);
          }
          return;
        }
        throw updateError;
      }

      setCars(cars.filter(car => car.id !== id));
      if (mainCarId === id) {
        setMainCarId(cars.length > 1 ? cars.find(car => car.id !== id)?.id || null : null);
      }
    } catch (error) {
      console.error('Error deleting car:', error);
      throw error;
    }
  };

  const restoreCar = async (id: string) => {
    if (!user) {
      throw new Error('User not authenticated');
    }
    try {
      const { error } = await supabase
        .from('cars')
        .update({ deleted_at: null })
        .eq('id', id);

      if (error) throw error;
      await fetchCarsFromSupabase();
    } catch (error) {
      console.error('Error restoring car:', error);
      throw error;
    }
  };

  const setMainCar = (id: string | null) => {
    setMainCarId(id);
  };

  const getMainCar = () => {
    return cars.find(car => car.id === mainCarId);
  };

  const addIssue = async (carId: string, issue: Omit<CarIssue, 'id'>): Promise<string> => {
    const newIssueId = crypto.randomUUID();
    
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Prepare symptom data for database
      const symptomData = {
        id: newIssueId,
        user_id: user.id,
        car_id: carId,
        title: issue.title,
        description: issue.description,
        ai_summary: issue.aiSummary,
        urgency_level: issue.urgencyLevel || 3, // Default to medium urgency
        severity: issue.urgencyLevel || 3, // Map urgency to severity for now
        ai_questions: issue.aiQuestions || [],
        ai_answers: issue.aiAnswers || [],
        ai_causes: issue.aiCauses || [],
        ai_recommendations: issue.aiRecommendations || [],
        location: issue.location,
        possible_causes: issue.possibleCauses || [],
        diy_check: issue.diyCheck,
        explanation: issue.explanation,
        status: issue.status || 'draft', // Top-level status column
        context: {
          notes: issue.notes,
          resolvedBy: issue.resolvedBy,
        }
      };

      console.log('💾 Saving symptom to database:', {
        symptomId: newIssueId,
        carId,
        userId: user.id,
        hasImages: issue.images?.length || 0
      });

      // Insert into symptoms table
      const { error: symptomError } = await supabase
        .from('symptoms')
        .insert(symptomData);

      if (symptomError) {
        console.error('Failed to save symptom to database:', symptomError);
        throw new Error(`Database error: ${symptomError.message}`);
      }

      // Save images to media table if any
      // (REMOVED: handled by finalize-analysis Edge Function)
      // if (issue.images && issue.images.length > 0) {
      //   const { saveSymptomImagesToDatabase } = await import('@/lib/uploadSymptomImages');
      //   const uploadedImages = issue.images.map((gcsPath, index) => ({
      //     gcsPath,
      //     signedUrl: '', // Will be generated when needed
      //     index
      //   }));
      //   await saveSymptomImagesToDatabase(newIssueId, uploadedImages);
      // }

      // Update local state
      setCars(cars.map(car => {
        if (car.id === carId) {
          const newIssue = { ...issue, id: newIssueId };
          const updatedIssues = [...(car.issues || []), newIssue];
          
          return { 
            ...car, 
            issues: updatedIssues
          };
        }
        return car;
      }));

      console.log('✅ Symptom saved successfully:', newIssueId);
      return newIssueId;
    } catch (error) {
      console.error('💥 Failed to save symptom:', error);
      throw error;
    }
  };

  const addIssueToCar = (carId: string, issue: CarIssue) => {
    setCars(cars.map(car => {
      if (car.id === carId) {
        const updatedIssues = [...(car.issues || []), issue];
        return {
          ...car,
          issues: updatedIssues
        };
      }
      return car;
    }));
  };

  const updateIssue = async (carId: string, issueId: string, updates: Partial<CarIssue>) => {
    // Update local state immediately for responsive UI
    setCars(cars.map(car => {
      if (car.id === carId && car.issues) {
        const updatedIssues = car.issues.map(issue => 
          issue.id === issueId ? { ...issue, ...updates } : issue
        );
        
        return { 
          ...car, 
          issues: updatedIssues
        };
      }
      return car;
    }));

    // Persist changes to database
    try {
      const { error } = await supabase
        .from('symptoms')
        .update(updates)
        .eq('id', issueId);

      if (error) {
        console.error('Failed to update issue in database:', error);
        // Revert local state on error
        setCars(cars.map(car => {
          if (car.id === carId && car.issues) {
            const revertedIssues = car.issues.map(issue => 
              issue.id === issueId ? { ...issue, ...updates } : issue
            );
            
            return { 
              ...car, 
              issues: revertedIssues
            };
          }
          return car;
        }));
      } else {
        console.log('✅ Issue updated in database:', { issueId, updates });
      }
    } catch (error) {
      console.error('Error updating issue in database:', error);
    }
  };

  const deleteIssue = (carId: string, issueId: string) => {
    setCars(cars.map(car => {
      if (car.id === carId && car.issues) {
        const updatedIssues = car.issues.filter(issue => issue.id !== issueId);
        
        return { 
          ...car, 
          issues: updatedIssues
        };
      }
      return car;
    }));
  };

  const addRepair = (carId: string, repair: Omit<Repair, 'id'>) => {
    const newRepairId = crypto.randomUUID();
    
    setCars(cars.map(car => {
      if (car.id === carId) {
        const newRepair = { ...repair, id: newRepairId };
        const updatedRepairs = [...(car.repairs || []), newRepair];
        
        let updatedIssues = [...(car.issues || [])];
        
        if (repair.resolvedIssues?.length || repair.partiallyResolvedIssues?.length) {
          updatedIssues = updatedIssues.map(issue => {
            if (repair.resolvedIssues?.includes(issue.id)) {
              return { ...issue, status: 'resolved', resolvedBy: newRepairId };
            }
            if (repair.partiallyResolvedIssues?.includes(issue.id)) {
              return { ...issue, status: 'recurred', resolvedBy: newRepairId };
            }
            return issue;
          });
        }
        
        return { 
          ...car, 
          repairs: updatedRepairs,
          issues: updatedIssues
        };
      }
      return car;
    }));
    
    return newRepairId;
  };

  const updateRepair = (carId: string, repairId: string, updates: Partial<Repair>) => {
    setCars(cars.map(car => {
      if (car.id === carId) {
        const updatedRepairs = (car.repairs || []).map(repair => 
          repair.id === repairId ? { ...repair, ...updates } : repair
        );
        
        const updatedRepair = updatedRepairs.find(r => r.id === repairId);
        
        if (!updatedRepair || (!updatedRepair.resolvedIssues?.length && !updatedRepair.partiallyResolvedIssues?.length)) {
          return { ...car, repairs: updatedRepairs };
        }
        
        let updatedIssues = [...(car.issues || [])];
        
        updatedIssues = updatedIssues.map(issue => {
          if (issue.resolvedBy === repairId) {
            return { ...issue, status: 'open', resolvedBy: undefined };
          }
          return issue;
        });
        
        updatedIssues = updatedIssues.map(issue => {
          if (updatedRepair.resolvedIssues?.includes(issue.id)) {
            return { ...issue, status: 'resolved', resolvedBy: repairId };
          }
          if (updatedRepair.partiallyResolvedIssues?.includes(issue.id)) {
            return { ...issue, status: 'recurred', resolvedBy: repairId };
          }
          return issue;
        });
        
        return { 
          ...car, 
          repairs: updatedRepairs,
          issues: updatedIssues
        };
      }
      return car;
    }));
  };

  const deleteRepair = (carId: string, repairId: string) => {
    setCars(cars.map(car => {
      if (car.id === carId) {
        const updatedRepairs = (car.repairs || []).filter(repair => repair.id !== repairId);
        
        let updatedIssues = [...(car.issues || [])];
        updatedIssues = updatedIssues.map(issue => {
          if (issue.resolvedBy === repairId) {
            return { ...issue, status: 'open', resolvedBy: undefined };
          }
          return issue;
        });
        
        return { 
          ...car, 
          repairs: updatedRepairs,
          issues: updatedIssues
        };
      }
      return car;
    }));
  };

  const getRepair = (carId: string, repairId: string) => {
    const car = cars.find(car => car.id === carId);
    if (!car || !car.repairs) return undefined;
    return car.repairs.find(repair => repair.id === repairId);
  };

  const addRecurrence = (carId: string, issueId: string, recurrence: Omit<Recurrence, 'id'>) => {
    const recurrenceId = crypto.randomUUID();
    
    setCars(cars.map(car => {
      if (car.id === carId && car.issues) {
        const updatedIssues = car.issues.map(issue => {
          if (issue.id === issueId) {
            const recurrences = issue.recurrences || [];
            const newRecurrence = { ...recurrence, id: recurrenceId };
            
            return { 
              ...issue, 
              recurrences: [...recurrences, newRecurrence]
            };
          }
          return issue;
        });
        
        return { 
          ...car, 
          issues: updatedIssues
        };
      }
      return car;
    }));
    
    return recurrenceId;
  };

  const deleteRecurrence = (carId: string, issueId: string, recurrenceId: string) => {
    setCars(cars.map(car => {
      if (car.id === carId && car.issues) {
        const updatedIssues = car.issues.map(issue => {
          if (issue.id === issueId && issue.recurrences) {
            const updatedRecurrences = issue.recurrences.filter(rec => rec.id !== recurrenceId);
            
            return { 
              ...issue, 
              recurrences: updatedRecurrences
            };
          }
          return issue;
        });
        
        return { 
          ...car, 
          issues: updatedIssues
        };
      }
      return car;
    }));
  };

  // Car sharing helper functions
  const generateShareLink = async (carId: string): Promise<{ shareLink: string; expiresAt: string }> => {
    if (!user) throw new Error('User not authenticated');
    
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('No active session');

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-car-share-link`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ carId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to generate share link');
    }

    const result = await response.json();
    return { shareLink: result.shareLink, expiresAt: result.expiresAt };
  };

  const acceptShareLink = async (token: string): Promise<{ carId: string }> => {
    if (!user) throw new Error('User not authenticated');
    
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('No active session');

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/accept-car-share`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to accept share link');
    }

    const result = await response.json();
    // Refresh cars list to include the newly shared car
    await fetchCarsFromSupabase();
    return { carId: result.carId };
  };

  const removeSharedUser = async (carId: string, userIdToRemove?: string): Promise<void> => {
    if (!user) throw new Error('User not authenticated');
    
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('No active session');

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/remove-car-share`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ carId, userIdToRemove }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to remove user from car');
    }

    // Refresh cars list
    await fetchCarsFromSupabase();
  };

  const transferOwnership = async (carId: string, newOwnerId: string, removeFromOriginalOwner: boolean): Promise<void> => {
    if (!user) throw new Error('User not authenticated');
    
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('No active session');

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/transfer-car-ownership`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ carId, newOwnerId, removeFromOriginalOwner }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to transfer ownership');
    }

    // Refresh cars list
    await fetchCarsFromSupabase();
  };

  const value = {
    cars,
    mainCarId,
    addCar,
    updateCar,
    deleteCar,
    restoreCar,
    setMainCar,
    getMainCar,
    addIssue,
    addIssueToCar,
    updateIssue,
    deleteIssue,
    addRepair,
    updateRepair,
    deleteRepair,
    getRepair,
    addRecurrence,
    deleteRecurrence,
    fetchCarsFromSupabase,
    generateShareLink,
    acceptShareLink,
    removeSharedUser,
    transferOwnership,
  };

  return <CarContext.Provider value={value}>{children}</CarContext.Provider>;
};